import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CloseIcon,
  DatePicker,
  Drawer,
  Label,
  Text,
} from '@mercai/clever'

import { SupplierOrderDrawerFormProps } from './drawer-form.types'

import { onErrorMessage, onSuccessMessage } from '@/helpers'
import {
  QUERIES_ADMIN,
  useSupplierOrderDetail,
  useSupplierPaymentEdit,
} from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import styles from './drawer-form.module.css'

export const SupplierOrderDrawerForm = ({
  isOpen,
  onHandleOpen,
  uuid,
}: SupplierOrderDrawerFormProps) => {
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [expectedPaymentDate, setExpectedPaymentDate] = useState<
    string | undefined
  >()

  const { data } = useSupplierOrderDetail(uuid || '')

  const queryClient = useQueryClient()

  const { mutateAsync: mutateAsyncPayment, isPending: isPendingPayment } =
    useSupplierPaymentEdit(data?.data?.payments?.[0]?.uuid || '')

  const onSubmit = async () => {
    const responsePayment = await mutateAsyncPayment({
      expectedPaymentDate,
    })

    if (responsePayment.success) {
      onHandleOpen()
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module],
      })
      onSuccessMessage(
        t('drawerSupplierOrderSuccessTitle'),
        t('drawerSupplierOrderSuccessDescription'),
      )
      return null
    }

    onErrorMessage(
      t('drawerSupplierOrderErrorTitle'),
      t('drawerSupplierOrderErrorDescription'),
    )
  }

  useEffect(() => {
    if (data) {
      setExpectedPaymentDate(
        data?.data?.payments?.[0]?.expectedPaymentDate || undefined,
      )
    }
  }, [data])

  return (
    <Drawer isOpen={isOpen} onHandleOpen={onHandleOpen}>
      <div className={styles.header}>
        <Text variant="title-large" className={styles['header-title']}>
          {t('tableSupplierOrderDrawerEditTitle')}
        </Text>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div>
          <Label>{t('tableSupplierOrderDrawerEditExpectedPaidLabel')}</Label>
          <DatePicker
            value={expectedPaymentDate}
            onChange={(date) => setExpectedPaymentDate(date)}
            placeholder="dd/mm/yyyy"
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onHandleOpen}>
          {t('tableSupplierOrderDrawerEditCancelButton')}
        </Button>
        <Button
          onClick={onSubmit}
          isActive={!isPendingPayment}
          disabled={isPendingPayment}
        >
          {t('tableSupplierOrderDrawerEditConfirmButton')}
        </Button>
      </div>
    </Drawer>
  )
}
